import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';

import ProfitabilityContainer from '@totem/components/ticketing/details/profitability/profitabilityContainer';
import ProfitabilityLayout from '@totem/components/ticketing/details/profitability/profitabilityLayout';
import ProjectSummary from '@totem/components/ticketing/details/project/projectSummary';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { ticketStyles } from '@totem/components/ticketing/details/ticketStyles';
import { PartyInvolved } from '@totem/components/ticketing/ticketing';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';

const ProjectDetails = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);
  const [projectManagers, setProjectManagers] = useState<PartyInvolved[]>([]);

  useEffect(() => {
    if (isNotNull(ticket)) {
      const projMgrs = ticket.ticket.partiesInvolved.filter(
        (chk) => chk.status === 'ACTIVE' && chk.type === 'Project Manager',
      );
      setProjectManagers(projMgrs);
    }
  }, [ticket.ticket.partiesInvolved]);

  const budgetedHours = useMemo(() => {
    if (isNotNull(ticket) && isNotNull(ticket.ticket.tasks)) {
      const budgeted = ticket.ticket.tasks.reduce((accumulator, record) => {
        return accumulator + record.budgetedHours;
      }, 0);
      return budgeted;
    }
    return 0;
  }, [ticket.ticket.tasks]);

  const actualHours = useMemo(() => {
    return isNotNull(ticket) && isNotNull(ticket.ticket.timeEntries)
      ? ticket.ticket.timeEntries.reduce(
          (acc, item) => acc + item.hoursLogged,
          0,
        )
      : 0;
  }, [ticket.ticket?.timeEntries]);

  const actualHoursBilled = useMemo(() => {
    return isNotNull(ticket) && isNotNull(ticket.ticket.timeEntries)
      ? ticket.ticket.timeEntries
          .filter((chk) => chk.billable && chk.billingId !== '')
          .reduce((acc, item) => acc + item.hoursLogged, 0)
      : 0;
  }, [ticket.ticket?.timeEntries]);

  const elapsedDays = useMemo(() => {
    if (!isNotNull(ticket)) return 0;
    const start = dayjs(ticket.ticket.createdAt);
    const end =
      ticket.ticket.status === 'CLOSED'
        ? dayjs(ticket.ticket.closedAt)
        : dayjs();
    return end.diff(start, 'day');
  }, [ticket.ticket.status, ticket.ticket.createdAt, ticket.ticket.closedAt]);

  return (
    <div>
      <Row>
        <Col span={6}>
          <HeaderItem title="Project Status">
            {isNotNull(ticket.ticket.project)
              ? ticket.ticket.project.status
              : ''}
          </HeaderItem>
          <HeaderItem title="Project Managers">
            {projectManagers.map((pm) => (
              <div key={pm.id}>
                <UserOutlined style={{ color: ticketStyles.colors.positive }} />
                <a href={`mailto:${pm.email}`}>
                  <span style={{ fontWeight: 500, paddingLeft: '1rem' }}>
                    {pm.name}
                  </span>
                </a>
              </div>
            ))}
          </HeaderItem>
        </Col>
        <Col span={6}>
          <HeaderItem title="Start Date">
            {isNotNull(ticket.ticket.project)
              ? formatDateOnly(ticket.ticket.project.startDate)
              : ''}
          </HeaderItem>
          <HeaderItem title="Target Date">
            {isNotNull(ticket.ticket.project)
              ? formatDateOnly(ticket.ticket.project.endDate)
              : ''}
          </HeaderItem>
          <HeaderItem title="Elapsed Days">
            {elapsedDays} {elapsedDays === 1 ? 'day' : 'days'}
          </HeaderItem>
        </Col>
        <Col span={6}>
          <HeaderItem title="Billing Type">
            {isNotNull(ticket.ticket.project)
              ? ticket.ticket.project.billingType
              : ''}
          </HeaderItem>
          <HeaderItem title="Expenses Type">
            {isNotNull(ticket.ticket.project)
              ? ticket.ticket.project.expensesType
              : ''}
          </HeaderItem>
          <HeaderItem title="Fee">
            <Currency
              amount={
                isNotNull(ticket.ticket.project) ? ticket.ticket.project.fee : 0
              }
              currency={'USD'}
              showCurrency={false}
            />
          </HeaderItem>
        </Col>
        <Col span={6}>
          <HeaderItem title="Budgeted Hours">
            <Hours amount={budgetedHours} />
          </HeaderItem>
          <HeaderItem title="Actual Hours">
            <Hours amount={actualHours} />
          </HeaderItem>
          <HeaderItem title="Billed Hours">
            <Hours amount={actualHoursBilled} />
          </HeaderItem>
        </Col>
      </Row>
      <br />
      <br />
      <ProjectSummary />
      {permissions.internal.hasPermissions(userProfile) && (
        <>
          <br />
          <br />
          <ProfitabilityContainer ticketId={ticket.ticket.id}>
            <ProfitabilityLayout />
          </ProfitabilityContainer>
        </>
      )}
    </div>
  );
};

export default ProjectDetails;
